import apiClient from './client'
import type { AvailabilitySlot, MyAvailability } from './interviewer'

// ─────────────────────────────────────────────────────────────────────────────
// Native Scheduler api client (Increment 1 — interviewer availability authoring).
// Mirrors the style of interviewer.ts / sessions.ts: uses the shared axios
// apiClient, which AUTO-UNWRAPS the { data: T } envelope in a response
// interceptor and converts errors into Error(friendlyApiError(...)) — so 409/422
// server messages arrive as `err.message` at the call site.
// ─────────────────────────────────────────────────────────────────────────────

// ── Availability READ types ──────────────────────────────────────────────────
// Re-export the shared slot/my-availability types so callers can import
// everything availability-related from the scheduler module.
export type { AvailabilitySlot, MyAvailability } from './interviewer'

// One interviewer's open slots for a given date.
export interface InterviewerAvailability {
  interviewer_id: number
  alias: string
  avatar_url: string | null
  language: string | null
  interviewer_timezone: string
  slots: AvailabilitySlot[]
}

// GET /api/scheduler/availability/all
export interface AvailabilityAllResponse {
  interviewers: InterviewerAvailability[]
}

// GET /api/scheduler/availability/month
export interface MonthAvailabilityResponse {
  year: number
  month: number
  dates_with_slots: string[]
}

// Per-interviewer scheduler settings.
// GET/PUT /api/scheduler/config
export interface SchedulerConfig {
  timezone: string                     // IANA, e.g. "America/New_York"
  buffer_before_minutes: number        // one of {0, 30, 60, 120}
  buffer_after_minutes: number         // one of {0, 30, 60, 120}
  min_notice_hours: number             // lead time before a slot can be booked
  booking_window_days: number          // the STANDARD window (<= 45)
  slot_duration_minutes: number | null // null → platform default
  is_accepting_bookings: boolean
  admin_locked: boolean                // read-only; when true the toggle is locked

  // Derived, read-only — the standard window with any approved extension folded
  // in, so a screen can show what clients can actually book without a second
  // request. Optional so older/partial payloads still type-check.
  extra_days?: number                  // active approved extension, 0 if none
  effective_window_days?: number       // min(base + extra, 60)
  effective_until?: string | null      // today + effective_window_days
}

// A single recurring weekly availability window.
// day_of_week: 0 = Monday … 6 = Sunday.
export interface WeeklyRule {
  day_of_week: number
  start_time: string // "HH:MM"
  end_time: string   // "HH:MM"
  is_active: boolean
}

// A one-time override for a specific date.
// type "custom"  → open only the given start_time..end_time on that date.
// type "blocked" → day off (no times; start_time/end_time omitted).
export interface DateOverride {
  id: number
  date: string       // "YYYY-MM-DD"
  type: 'custom' | 'blocked'
  start_time?: string | null // "HH:MM" (required when type === "custom")
  end_time?: string | null   // "HH:MM"
}

// FR-IA-6 is WARN-BUT-ALLOW: availability mutations that leave an already-booked
// session outside the new availability still SUCCEED and carry a non-null
// `warning` describing the orphaned session(s). Null/absent when nothing orphaned.

// PUT /api/scheduler/availability/rules response.
export interface WeeklyRulesResult {
  rules: WeeklyRule[]
  warning?: string | null
}

// POST /api/scheduler/availability/overrides response (created override + warning).
export interface DateOverrideResult extends DateOverride {
  warning?: string | null
}

// DELETE /api/scheduler/availability/overrides/{id} response.
export interface DeleteOverrideResult {
  deleted: boolean
  warning?: string | null
}

// ── Availability extension (rolling EXTRA days, admin-approved, total ≤60) ────
// An interviewer requests N extra bookable days on top of their configured
// window; an admin approves/rejects/revokes. The grant is a day COUNT, not an
// end date, so it does not decay — the bookable range rolls forward daily.
// status ∈ pending | approved | rejected | revoked.
export interface ExtensionRequest {
  id: number
  interviewer_id: number
  interviewer_alias?: string | null
  extra_days: number
  status: 'pending' | 'approved' | 'rejected' | 'revoked'
  reason?: string | null
  admin_note?: string | null
  decided_by?: number | null
  decided_at?: string | null
  created_at: string
  updated_at: string
}

// GET /api/scheduler/extension-requests/me
export interface MyExtensionStatus {
  current_window_days: number   // configured base window (e.g. 45, or 20)
  extra_days: number            // active approved extra days (0 when none)
  effective_window_days: number // min(base + extra, 60)
  max_extra_days: number        // how many more days may still be requested
  effective_until: string       // today + effective_window_days, recomputed daily
  latest_request?: ExtensionRequest | null
}

export const schedulerApi = {
  // GET /api/scheduler/config
  getConfig: () =>
    apiClient
      .get('/scheduler/config')
      .then((r) => (r.data.data ?? r.data) as SchedulerConfig),

  // PUT /api/scheduler/config
  // Body accepts the full config; admin_locked is server-controlled and ignored.
  updateConfig: (config: Partial<SchedulerConfig>) =>
    apiClient
      .put('/scheduler/config', config)
      .then((r) => (r.data.data ?? r.data) as SchedulerConfig),

  // GET /api/scheduler/availability/rules
  // Tolerant of both { rules: [...] } and a bare array response shape.
  getRules: () =>
    apiClient.get('/scheduler/availability/rules').then((r) => {
      const d = r.data
      return (d?.rules ?? d?.data ?? d ?? []) as WeeklyRule[]
    }),

  // PUT /api/scheduler/availability/rules — replaces the full weekly rule set.
  // Returns the saved rules plus an optional FR-IA-6 `warning` (WARN-BUT-ALLOW).
  updateRules: (rules: WeeklyRule[]): Promise<WeeklyRulesResult> =>
    apiClient.put('/scheduler/availability/rules', { rules }).then((r) => {
      const d = r.data?.data ?? r.data
      return {
        rules: (d?.rules ?? d ?? []) as WeeklyRule[],
        warning: (d?.warning ?? null) as string | null,
      }
    }),

  // GET /api/scheduler/availability/overrides
  getOverrides: () =>
    apiClient.get('/scheduler/availability/overrides').then((r) => {
      const d = r.data
      return (d?.overrides ?? d?.data ?? d ?? []) as DateOverride[]
    }),

  // POST /api/scheduler/availability/overrides
  // Returns the created override plus an optional FR-IA-6 `warning` (the
  // blocked-orphan case now succeeds with a warning rather than a 409).
  createOverride: (override: Omit<DateOverride, 'id'>): Promise<DateOverrideResult> =>
    apiClient
      .post('/scheduler/availability/overrides', override)
      .then((r) => (r.data?.data ?? r.data) as DateOverrideResult),

  // DELETE /api/scheduler/availability/overrides/{id}
  // Succeeds even when the removed window still backed a booking; the response
  // then carries a non-null FR-IA-6 `warning` (WARN-BUT-ALLOW).
  deleteOverride: (id: number): Promise<DeleteOverrideResult> =>
    apiClient.delete(`/scheduler/availability/overrides/${id}`).then((r) => {
      const d = r.data?.data ?? r.data
      return {
        deleted: (d?.deleted ?? true) as boolean,
        warning: (d?.warning ?? null) as string | null,
      }
    }),

  // ── Availability READS (client booking flow) ──────────────────────────────
  // GET /api/scheduler/availability/all?date&timezone&language
  // All interviewers with open slots on the given date.
  getAvailabilityAll: (date: string, timezone?: string, language?: string) =>
    apiClient
      .get('/scheduler/availability/all', {
        params: {
          date,
          timezone: timezone || 'UTC',
          ...(language ? { language } : {}),
        },
      })
      .then((r) => (r.data.data ?? r.data) as AvailabilityAllResponse),

  // GET /api/scheduler/availability/month?year&month
  // The set of YYYY-MM-DD dates in the month that have at least one open slot.
  getMonthAvailability: (year: number, month: number) =>
    apiClient
      .get('/scheduler/availability/month', { params: { year, month } })
      .then((r) => (r.data.data ?? r.data) as MonthAvailabilityResponse),

  // GET /api/scheduler/availability?interviewer_id&date&timezone
  // A single interviewer's open slots for the given date.
  getAvailability: (interviewerId: number, date: string, timezone?: string) =>
    apiClient
      .get('/scheduler/availability', {
        params: {
          interviewer_id: interviewerId,
          date,
          timezone: timezone || 'UTC',
        },
      })
      .then((r) => (r.data.data ?? r.data) as InterviewerAvailability),

  // GET /api/scheduler/my-availability?start&days
  // The authenticated interviewer's own open bookable slots, localized to their
  // tz. `connected` is true when native config/rules exist.
  getMyAvailability: (start: string, days = 7) =>
    apiClient
      .get('/scheduler/my-availability', { params: { start, days } })
      .then((r) => (r.data.data ?? r.data) as MyAvailability),

  // ── Availability extension (interviewer-facing) ───────────────────────────
  // POST /api/scheduler/extension-requests — ask for N extra bookable days.
  requestExtension: (body: { extra_days: number; reason: string }) =>
    apiClient
      .post('/scheduler/extension-requests', body)
      .then((r) => (r.data.data ?? r.data) as ExtensionRequest),

  // POST /api/scheduler/extension-requests/me/cancel — give up your own extended
  // window. Withdraws a pending request, or hands back an approved one so the
  // bookable window reverts to the standard booking_window_days. No path id: the
  // endpoint always acts on the calling interviewer's own request.
  cancelMyExtension: () =>
    apiClient
      .post('/scheduler/extension-requests/me/cancel')
      .then((r) => (r.data.data ?? r.data) as ExtensionRequest),

  // GET /api/scheduler/extension-requests/me — current window + latest request.
  getMyExtensionStatus: () =>
    apiClient
      .get('/scheduler/extension-requests/me')
      .then((r) => (r.data.data ?? r.data) as MyExtensionStatus),
}
